import { useEffect, useRef } from 'react'

import * as amplitude from '@amplitude/analytics-browser'
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser'
import { INNOSCRIPTA_INTERNAL_ORGANIZATION_ID } from 'shared/constants/user'

import { UseAnalyticsServiceHook } from '../analytics.types'

const sessionReplayTracking = sessionReplayPlugin({
  forceSessionTracking: true,
  sampleRate: 0.1
})

export const useAmplitude: UseAnalyticsServiceHook<'amplitude'> = (
  user,
  config = {
    enabled: true,
    apiKey: ''
  }
) => {
  const isReplayAlreadyAdded = useRef(false)

  useEffect(() => {
    if (!config.apiKey) return

    amplitude.setOptOut(!config.enabled)

    if (!config.enabled) return

    const isInternalUser =
      user?.organization_id?.toString() ===
      `${INNOSCRIPTA_INTERNAL_ORGANIZATION_ID}`

    if (isInternalUser && !isReplayAlreadyAdded.current) {
      amplitude.add(sessionReplayTracking)
      isReplayAlreadyAdded.current = true
    }

    amplitude.init(config.apiKey, {
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: true
      },
      serverZone: 'EU',
      minIdLength: 0
    })
  }, [config.apiKey, config.enabled, user?.organization_id])

  useEffect(() => {
    if (!config.apiKey || !config.enabled || typeof user?.id !== 'number')
      return

    const identify = new amplitude.Identify()

    identify.set('user_id', `${user.id}`)
    identify.set('email', user.email)
    identify.set('name', user.name)
    identify.set('organization_id', `${user.organization_id}`)

    amplitude.setUserId(`${user.id}`)
    amplitude.identify(identify, {
      user_id: `${user.id}`,
      extra: {
        name: user.name,
        email: user.email,
        organization_id: user.organization_id
      }
    })
  }, [user, config.apiKey, config.enabled])
}
